import { z } from "zod"

/**
 * Stable, machine-readable error codes for /api/v1 (ADR-010, ADR-015). Clients branch on
 * these; never rename one, only add new codes.
 */
export const API_ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "VEHICLE_ARCHIVED",
  "QUOTA_EXCEEDED",
  "PERIOD_LOCKED",
  "TRIP_OVERLAP",
  "INTERNAL",
  // Sync push (T130): a referenced parent (vehicle, trip) is not on the server yet; retry later.
  "DEPENDENCY_MISSING",
  // The record was soft-deleted; edits to it are discarded (delete wins, ADR-015 section 3).
  "GONE",
  "PAYLOAD_TOO_LARGE",
  "RATE_LIMITED",
  // GET /changes (T131): the cursor points into history the retention job has compacted;
  // `details.floor_seq` names the floor. The client pulls everything again from since=0.
  "CURSOR_EXPIRED",
  // The plan does not include the feature (e.g. receipts); upgrading unlocks it. Distinct from
  // QUOTA_EXCEEDED (a usage limit inside an included feature).
  "UPGRADE_REQUIRED",
  // Account deletion (T322): the account is scheduled for deletion; only /me, /account/restore,
  // /account/delete and /export answer until it is restored or purged. Clients wipe local data.
  "ACCOUNT_DELETION_PENDING",
  // Retention (T784): the organisation's data was removed from the account after a plan lapse
  // (day 90) and is only restorable by paying until the hard delete. Distinct from
  // ACCOUNT_DELETION_PENDING so clients do NOT wipe local data (phone-only trips may exist).
  "ORGANISATION_SOFT_DELETED",
  // The organisation has more than one member, so the owner cannot delete it alone.
  "ORGANISATION_HAS_MEMBERS",
  // Language assistant (T341): every model provider failed; retry later and keep the draft.
  "PROVIDER_UNAVAILABLE",
  // Teams sync push (ADR-017 2.10): the vehicle was un-assigned before the trip started, or the
  // row is not visible to the caller's role. Clients treat an unknown code as a plain rejection.
  "VEHICLE_NOT_ASSIGNED",
  "NOT_VISIBLE",
  // Sync push (T1042 device-user binding): the request's `device_id` is bound to another user.
  // Whole request, never per entry. Clients mint a new id for the signed-in user once and retry;
  // older clients treat an unknown 403 as a deferred batch and never delete local data.
  "DEVICE_BOUND",
  // A team organisation holds as many vehicles as D-TEAMVEH-CAP allows, max(5, ceil(1.5 x Member
  // seats)) (T1255). Creating or restoring one more is refused (409); a sync push answers `held`
  // with this code. Existing vehicles stay readable. Older clients treat an unknown code as a
  // plain refusal. `details` is `teamVehicleCapDetailsSchema`.
  "TEAM_VEHICLE_CAP",
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

/**
 * Codes a sync push answers with `held` (kept on the device, resent later) instead of `rejected`:
 * an entitlement or a limit, never a defect of the entry. `details` rides along when present.
 */
export const HELD_ERROR_CODES = [
  "QUOTA_EXCEEDED",
  "UPGRADE_REQUIRED",
  "TEAM_VEHICLE_CAP",
] as const satisfies readonly ApiErrorCode[]

export const isHeldErrorCode = (code: ApiErrorCode): boolean =>
  (HELD_ERROR_CODES as readonly string[]).includes(code)

export const apiErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.enum(API_ERROR_CODES),
    message: z.string(),
    details: z.unknown().optional(),
  }),
})

export type ApiErrorEnvelope = z.infer<typeof apiErrorEnvelopeSchema>

/**
 * Which push bucket refused a 429 RATE_LIMITED (`details.scope`, T1042). Optional on the wire and
 * lenient on read: an absent or unknown scope means "back off as before".
 */
export const RATE_LIMIT_SCOPES = ["user", "organisation", "device"] as const

export const rateLimitedDetailsSchema = z.looseObject({
  scope: z.string().optional(),
})

export type RateLimitedDetails = z.infer<typeof rateLimitedDetailsSchema>

/**
 * `details` of a `TEAM_VEHICLE_CAP` error (T1255). All optional and lenient: a client shows its own
 * plain message and reads the numbers only when present.
 */
export const teamVehicleCapDetailsSchema = z.looseObject({
  feature: z.string().optional(),
  reason: z.string().optional(),
  /** Vehicles the team may hold. */
  cap: z.number().optional(),
  /** Member-role seats the cap was computed from. */
  member_seats: z.number().optional(),
  /** Active vehicles the team holds now. */
  vehicles: z.number().optional(),
})

export type TeamVehicleCapDetails = z.infer<typeof teamVehicleCapDetailsSchema>

const HTTP_STATUS: Record<ApiErrorCode, number> = {
  VALIDATION_FAILED: 422,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VEHICLE_ARCHIVED: 409,
  // The free-tier limit is reached; upgrading lifts it.
  QUOTA_EXCEEDED: 402,
  PERIOD_LOCKED: 409,
  // A manual trip's time span intersects another trip of the organisation; details name it.
  TRIP_OVERLAP: 409,
  INTERNAL: 500,
  DEPENDENCY_MISSING: 409,
  GONE: 410,
  PAYLOAD_TOO_LARGE: 413,
  // The response carries a Retry-After header (seconds).
  RATE_LIMITED: 429,
  CURSOR_EXPIRED: 410,
  UPGRADE_REQUIRED: 402,
  ACCOUNT_DELETION_PENDING: 410,
  ORGANISATION_SOFT_DELETED: 410,
  ORGANISATION_HAS_MEMBERS: 409,
  PROVIDER_UNAVAILABLE: 503,
  VEHICLE_NOT_ASSIGNED: 403,
  NOT_VISIBLE: 403,
  DEVICE_BOUND: 403,
  TEAM_VEHICLE_CAP: 409,
}

export function httpStatusForErrorCode(code: ApiErrorCode): number {
  return HTTP_STATUS[code]
}
